import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { SelectField } from "@/components/SelectField/SelectField";

import {
	CreateObjection,
	CreateObjectionSchema,
	ObjectionCategory,
} from "../schema/objectionHandling";

interface AddObjectionModalProps {
	isOpen: boolean;
	categories: ObjectionCategory[];
	defaultCategoryId?: number | null;
	onClose: () => void;
	onCreateWithAi: (data: CreateObjection) => void;
	onCreateManual: (data: CreateObjection) => void;
}

export const AddObjectionModal: FunctionComponent<AddObjectionModalProps> = ({
	isOpen,
	categories,
	defaultCategoryId,
	onClose,
	onCreateWithAi,
	onCreateManual,
}) => {
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
		defaultCategoryId ?? categories[0]?.id ?? null
	);

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<CreateObjection>({
		defaultValues: {
			categoryId: defaultCategoryId ?? categories[0]?.id,
			quote: "",
			underlyingConcern: "",
			initialStrategyName: "",
			initialApproach: "",
			initialExampleReply: "",
		},
		resolver: zodResolver(CreateObjectionSchema),
	});

	useEffect(() => {
		if (isOpen) {
			const catId = defaultCategoryId ?? categories[0]?.id;

			setSelectedCategoryId(catId ?? null);
			reset({
				categoryId: catId,
				quote: "",
				underlyingConcern: "",
				initialStrategyName: "",
				initialApproach: "",
				initialExampleReply: "",
			});
		}
	}, [isOpen, defaultCategoryId, categories, reset]);

	useEffect(() => {
		if (selectedCategoryId !== null) {
			setValue("categoryId", selectedCategoryId);
		}
	}, [selectedCategoryId, setValue]);

	const onSubmitWithAi = (data: CreateObjection) => {
		onCreateWithAi(data);
		onClose();
	};

	const onSubmitManual = (data: CreateObjection) => {
		onCreateManual(data);
		onClose();
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmitWithAi)}
			handleSecondSave={handleSubmit(onSubmitManual)}
			isOpened={isOpen}
			secondSubmitButtonText="Create without AI"
			submitButtonText="Create with AI strategies"
			title="Add Objection"
		>
			<div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
				{/* Category */}
				<div>
					<SelectField
						handleSelect={(value) => setSelectedCategoryId(value as number)}
						label="Category *"
						options={categories.map((cat) => ({
							value: cat.id,
							title: cat.name,
						}))}
						selectedOption={selectedCategoryId}
					/>
					{errors.categoryId && (
						<p className="mt-1 text-xs text-red-500">{errors.categoryId.message}</p>
					)}
				</div>

				{/* Objection Quote */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">Objection Quote *</label>
					<textarea
						className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
						placeholder='e.g. "Your product doesn&apos;t integrate with our CRM"'
						{...register("quote")}
					/>
					{errors.quote && <p className="mt-1 text-xs text-red-500">{errors.quote.message}</p>}
				</div>

				{/* Underlying Concern */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Underlying Concern *
					</label>
					<textarea
						className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
						placeholder="What is the real concern behind this objection?"
						{...register("underlyingConcern")}
					/>
					{errors.underlyingConcern && (
						<p className="mt-1 text-xs text-red-500">{errors.underlyingConcern.message}</p>
					)}
				</div>

				{/* Optional initial strategy */}
				<div className="border-t border-gray-200 pt-4">
					<p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
						Initial Handling Strategy (optional)
					</p>
					<div className="space-y-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Strategy Name</label>
							<input
								className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
								placeholder="e.g. Highlight Integration Ecosystem"
								type="text"
								{...register("initialStrategyName")}
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Approach</label>
							<textarea
								className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
								placeholder="Describe the approach..."
								{...register("initialApproach")}
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Example Reply</label>
							<textarea
								className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
								placeholder="A sample reply to this objection..."
								{...register("initialExampleReply")}
							/>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};
